import { useLayoutEffect, useRef, useMemo } from 'react';
import {
  useDynamicHeaderSafe,
  DynamicHeaderConfig,
  HeaderStat,
  HeaderFilter,
  HeaderTab,
  HeaderAction
} from '../contexts/DynamicHeaderContext';
import { useWarehouseSectionLabel } from '../components/magazzino/warehouseMenuConfig';

/**
 * Hook per registrare la configurazione dell'header dinamico da una pagina.
 *
 * L'header viene registrato al mount e rimosso automaticamente all'unmount.
 * Supporta aggiornamenti dinamici della configurazione.
 *
 * @example
 * ```tsx
 * usePageHeader({
 *   sectionId: 'project-detail',
 *   title: project.name,
 *   showBackButton: true,
 *   onBack: () => navigate('/projects'),
 *   tabs: [
 *     { id: 'tasks', label: 'Task' },
 *     { id: 'brief', label: 'Brief' },
 *   ],
 *   activeTab,
 *   onTabChange: setActiveTab,
 * });
 * ```
 */
export function usePageHeader(config: DynamicHeaderConfig | null) {
  // Risolve automaticamente il titolo per le sezioni warehouse
  const resolvedLabel = useWarehouseSectionLabel(config?.sectionId ?? '');
  const resolvedConfig = useMemo(() => {
    if (!config || !config.sectionId) return config;
    // Se il titolo è hardcoded e il sectionId corrisponde a una voce warehouse,
    // usa la label risolta (custom o default dell'org)
    if (resolvedLabel && resolvedLabel !== config.sectionId) {
      return { ...config, title: resolvedLabel };
    }
    return config;
  }, [config, resolvedLabel]);

  const context = useDynamicHeaderSafe();

  // Estrai setConfig/removeConfig dal context per usarli come dipendenze stabili
  const setConfig = context?.setConfig;
  const removeConfig = context?.removeConfig;

  // Usa un ref per tracciare il config corrente
  const configRef = useRef(resolvedConfig);
  configRef.current = resolvedConfig;

  // Tieni traccia del sectionId registrato per pop puntuale all'unmount
  // (lo stack del context permette drawer sovrapposti senza perdere la
  // config sottostante)
  const registeredSectionIdRef = useRef<string | null>(null);

  // Traccia se siamo montati
  const isMountedRef = useRef(false);

  // Crea una chiave stabile per determinare quando aggiornare il config
  // Questo evita loop infiniti causati da oggetti/JSX ricreati ad ogni render
  const configKey = useMemo(() => {
    if (!resolvedConfig) return null;
    return JSON.stringify({
      sectionId: resolvedConfig.sectionId,
      title: resolvedConfig.title,
      subtitle: resolvedConfig.subtitle,
      activeTab: resolvedConfig.activeTab,
      currentViewMode: resolvedConfig.currentViewMode,
      showBackButton: resolvedConfig.showBackButton,
      // Serializza solo i valori dei filtri, non le funzioni onChange
      filterValues: resolvedConfig.filters?.map(f => ({
        id: f.id,
        value: f.value,
        // Includi anche i valori specifici del filtro date-period
        dateField: f.dateField,
        periodType: f.periodType,
        selectedYear: f.selectedYear,
        selectedQuarter: f.selectedQuarter,
        dateFrom: f.dateFrom,
        dateTo: f.dateTo,
        // Anche le opzioni: quelle calcolate dai dati (es. U.M. dai prodotti
        // caricati) arrivano dopo il primo render, e senza questa chiave il
        // menu restava vuoto per sempre
        optionsKey: f.options?.map(o => `${o.value}:${o.label}`).join('|'),
      })),
      // Serializza solo gli id delle stats, non i React elements
      statIds: resolvedConfig.stats?.map(s => ({ id: s.id, value: s.value })),
      // Serializza gli id delle tab per rilevare cambiamenti
      tabIds: resolvedConfig.tabs?.map(t => t.id),
      // Anche le viste: alcune compaiono dopo il primo render (es. "Team" quando arrivano i calendari condivisi)
      viewModeIds: (resolvedConfig as any).viewModes?.map((v: { id: string }) => v.id),
      // Serializza il sort config - usa valori primitivi per rilevare cambiamenti
      currentSortField: resolvedConfig.currentSort?.field,
      currentSortDirection: resolvedConfig.currentSort?.direction,
      // Chiave per forzare aggiornamento quando customLeft cambia
      customLeftKey: resolvedConfig.customLeftKey,
      customRightKey: resolvedConfig.customRightKey,
      customFiltersRowKey: resolvedConfig.customFiltersRowKey,
      // Serializza solo i campi primitivi delle actions (id/label/variant/hidden),
      // non onClick/icon: altrimenti le actions il cui `hidden` dipende da uno
      // stato di selezione (es. riga selezionata in lista) restano "congelate"
      // al valore del primo render, perché configKey non cambierebbe mai.
      actionsState: resolvedConfig.actions?.map(a => ({
        id: a.id,
        label: a.label,
        variant: a.variant,
        hidden: a.hidden,
      })),
      primaryActionState: resolvedConfig.primaryAction
        ? {
            id: resolvedConfig.primaryAction.id,
            label: resolvedConfig.primaryAction.label,
            variant: resolvedConfig.primaryAction.variant,
            hidden: resolvedConfig.primaryAction.hidden,
          }
        : undefined,
    });
  }, [
    resolvedConfig?.sectionId,
    resolvedConfig?.title,
    resolvedConfig?.subtitle,
    resolvedConfig?.activeTab,
    resolvedConfig?.currentViewMode,
    resolvedConfig?.showBackButton,
    resolvedConfig?.filters,
    resolvedConfig?.stats,
    resolvedConfig?.currentSort?.field,
    resolvedConfig?.currentSort?.direction,
    resolvedConfig?.customLeftKey,
    resolvedConfig?.customRightKey,
    resolvedConfig?.customFiltersRowKey,
    resolvedConfig?.tabs?.length,
    resolvedConfig?.actions,
    resolvedConfig?.primaryAction,
  ]);

  // Mount ID to force re-registration on remount
  const mountIdRef = useRef(0);

  // Track whether this hook ever registered a config (to avoid cleanup for null configs)
  const didRegisterRef = useRef(false);

  // Aggiorna il config quando i dati significativi cambiano
  // Usa useLayoutEffect per aggiornamenti sincroni prima del paint
  // Dipende da `configKey` (stringa stabile) per evitare loop infiniti
  // causati da oggetti/JSX ricreati ad ogni render
  useLayoutEffect(() => {
    if (!setConfig) return;

    if (configRef.current) {
      // Stessa pagina, sezione diversa (es. Magazzino: Prodotti -> Categorie): la config della sezione
      // di prima va tolta, altrimenti resta nello stack e, tornando a quella sezione, la sua config
      // viene aggiornata "sotto" e l'header continua a mostrare l'ultima sezione aperta
      const previous = registeredSectionIdRef.current;
      if (removeConfig && previous && didRegisterRef.current && previous !== configRef.current.sectionId) {
        removeConfig(previous);
      }
      setConfig(configRef.current);
      registeredSectionIdRef.current = configRef.current.sectionId;
      // Tracking della prima registrazione: il config può diventare non-null
      // DOPO il mount (es. dopo fetch dati). Senza questo flag, il cleanup
      // di mount non chiamerebbe removeConfig e l'header resterebbe sticky.
      didRegisterRef.current = true;
    } else if (removeConfig && registeredSectionIdRef.current && didRegisterRef.current) {
      // Config diventato null — rimuovi il sectionId precedente dallo stack
      // (es. MagazzinoMain passa null quando la sezione attiva è custom)
      removeConfig(registeredSectionIdRef.current);
      registeredSectionIdRef.current = null;
      didRegisterRef.current = false;
    }
  }, [setConfig, removeConfig, configKey]);

  // Mount/unmount: registra config e pulisci all'unmount
  // Usa useLayoutEffect (non useEffect) per garantire che il cleanup corra
  // PRIMA del setup della nuova pagina durante la navigazione, evitando
  // un frame con config=null che causa il gap tra header e contenuto
  useLayoutEffect(() => {
    isMountedRef.current = true;

    // Always register config on mount to handle remount scenarios
    if (setConfig && configRef.current) {
      setConfig(configRef.current);
      registeredSectionIdRef.current = configRef.current.sectionId;
      didRegisterRef.current = true;
    }

    return () => {
      isMountedRef.current = false;
      // Pop questa specifica sectionId dallo stack: l'header tornerà
      // automaticamente alla config sotto (es. drawer Lead sotto al
      // drawer Deal che si è appena chiuso).
      if (removeConfig && didRegisterRef.current && registeredSectionIdRef.current) {
        removeConfig(registeredSectionIdRef.current);
      }
    };
  }, [setConfig, removeConfig]);

  // No-op functions when provider is not available
  const noOp = () => {};
  const noOpUpdate = (_: Partial<DynamicHeaderConfig>) => {};

  // Funzioni utility per aggiornamenti parziali
  return {
    /**
     * Aggiorna solo il titolo
     */
    updateTitle: context ? (title: string) => context.updateConfig({ title }) : noOp,

    /**
     * Aggiorna solo il sottotitolo
     */
    updateSubtitle: context ? (subtitle: string | undefined) => context.updateConfig({ subtitle }) : noOp,

    /**
     * Aggiorna le stats
     */
    updateStats: context ? (stats: HeaderStat[]) => context.updateConfig({ stats }) : noOp,

    /**
     * Aggiorna i filtri
     */
    updateFilters: context ? (filters: HeaderFilter[]) => context.updateConfig({ filters }) : noOp,

    /**
     * Aggiorna le tabs
     */
    updateTabs: context ? (tabs: HeaderTab[]) => context.updateConfig({ tabs }) : noOp,

    /**
     * Aggiorna la tab attiva
     */
    updateActiveTab: context ? (activeTab: string) => context.updateConfig({ activeTab }) : noOp,

    /**
     * Aggiorna la view mode
     */
    updateViewMode: context ? (currentViewMode: string) => context.updateConfig({ currentViewMode }) : noOp,

    /**
     * Aggiorna le azioni
     */
    updateActions: context ? (actions: HeaderAction[]) => context.updateConfig({ actions }) : noOp,

    /**
     * Aggiorna qualsiasi parte della configurazione
     */
    update: context ? context.updateConfig : noOpUpdate,
  };
}
